import { DieTypes, isAnimationsDisabled, isDebug } from "@/constants";
import {
  extractAnimationKeyframes,
  FPS,
} from "@/helpers/animations/extractAnimationKeyframes";
import {
  DataTypes,
  type Animation,
  type AnimationGroup,
  type DieType,
  type RandomTrigger,
  type Trigger,
} from "@/types";
import { useMemo, type RefObject } from "react";
import {
  AnimationClip,
  BooleanKeyframeTrack,
  ColorKeyframeTrack,
  NumberKeyframeTrack,
  Object3D,
  VectorKeyframeTrack,
  type KeyframeTrack,
} from "three";
import { getAnimationTarget } from "../helpers/getAnimationTarget";

const getPath = (nodeName: string, property: string) => {
  if (property === "scaling") {
    return `${nodeName}.scale`;
  }

  if (property === "visibility") {
    return `${nodeName}.visible`;
  }

  if (property === "material.alpha") {
    return `${nodeName}.material.opacity`;
  }

  if (property === "material.transparencyMode") {
    return `${nodeName}.material.transparent`;
  }

  if (property === "material.albedoColor") {
    return `${nodeName}.material.color`;
  }

  if (property === "material.emissiveColor") {
    return `${nodeName}.material.emissive`;
  }

  if (property === "material.metallic") {
    return `${nodeName}.material.metalness`;
  }

  if (property === "material.Cut.value") {
    return `${nodeName}.material.u_Cut`;
  }

  if (property === "material.albedoTexture.uOffset") {
    return `${nodeName}.material.mapOffset[x]`;
  }

  if (property === "material.albedoTexture.vOffset") {
    return `${nodeName}.material.mapOffset[y]`;
  }

  if (property === "material.opacityTexture.uOffset") {
    return `${nodeName}.material.alphaMapOffset[x]`;
  }

  if (property === "material.opacityTexture.vOffset") {
    return `${nodeName}.material.alphaMapOffset[y]`;
  }

  if (property === "material.emissiveTexture.uOffset") {
    return `${nodeName}.material.emissiveMapOffset[x]`;
  }

  if (property === "material.emissiveTexture.vOffset") {
    return `${nodeName}.material.emissiveMapOffset[y]`;
  }

  if (property === "material.attachedBlocks.31.value") {
    return `${nodeName}.material.u_Removal`;
  }

  if (property === "material.attachedBlocks.27.value") {
    return `${nodeName}.material.u_Alpha`;
  }

  if (property === "material.attachedBlocks.9.value") {
    return `${nodeName}.material.u_Scale`;
  }

  return `${nodeName}.${property}`;
};

const buildTrack = (
  animation: Animation,
  nodeName: string,
  times: number[],
) => {
  const { property, dataType } = animation;
  const path = getPath(nodeName, property);
  const { values } = extractAnimationKeyframes(animation);

  if (property === "visibility") {
    return new BooleanKeyframeTrack(
      path,
      times,
      values.map((v) => (v > 0 ? true : false)),
    );
  }

  // For the sake of simplicity this seems to do the trick
  // Tested with Elder Heart die removal which is the only current use case
  if (property === "material.transparencyMode") {
    return [
      new BooleanKeyframeTrack(
        path,
        times,
        values.map((v) => (v > 0 ? true : false)),
      ),
      new BooleanKeyframeTrack(
        `${nodeName}.material.depthWrite`,
        times,
        values.map((v) => (v > 0 ? false : true)),
      ),
    ];
  }

  // TODO: support animating our built-in shader uniforms
  if (property === "material.glitterScale") {
    if (isDebug) {
      console.warn("Skipping unsupported glitterScale animation.");
    }

    return null;
  }

  switch (dataType) {
    case DataTypes.VECTOR: {
      return new VectorKeyframeTrack(path, times, values);
    }
    case DataTypes.COLOR: {
      return new ColorKeyframeTrack(path, times, values);
    }
    case DataTypes.NUMBER:
    default: {
      return new NumberKeyframeTrack(path, times, values);
    }
  }
};

/**
 * Correlate mesh's animations with the manifest's resources.
 *
 * todo: animation groups is a confusing name
 * @param root The node to apply animations to
 * @param animationGroups The manifest's animation resources (json files)
 * @param meshAnimations The animations applied to this specific node
 * @returns A map of AnimationClips by their manifest definition
 */
export const useTargetedAnimations = (
  setId: string,
  root: RefObject<Object3D>,
  dieType: DieType,
  upsideValue?: number,
  animationGroups?: Record<string, AnimationGroup>,
  meshAnimations?: (Trigger | RandomTrigger)[],
) => {
  const triggers = useMemo(
    () =>
      isAnimationsDisabled
        ? []
        : (meshAnimations?.reduce<Trigger[]>((acc, trigger) => {
            if (Array.isArray(trigger)) {
              trigger.forEach((randomTrigger) => {
                // the random animation groups can also be an array of triggers
                acc.push(
                  ...(Array.isArray(randomTrigger)
                    ? randomTrigger
                    : [randomTrigger]),
                );
              });
            } else {
              acc.push(trigger as Trigger);
            }

            return acc;
          }, []) ?? []),
    [meshAnimations],
  );

  const rootObject = root.current;

  const clips = useMemo(
    () =>
      isAnimationsDisabled
        ? []
        : triggers
            ?.map((trigger) => {
              const group = animationGroups?.[trigger.definition];
              if (!group) return;

              const tracks: KeyframeTrack[] = [];
              const endFrame = group.to ?? 0;

              group.targetedAnimations.forEach((targetedAnimation) => {
                // clone animation for metadata adjustments
                const animation = { ...targetedAnimation.animation };

                // 1. Find the target node
                const targetName = getAnimationTarget(
                  setId,
                  rootObject,
                  targetedAnimation.targetId,
                  animation,
                );
                if (!targetName) return;

                // 2. Map keyframes to time based on FPS for consistency across devices
                const times = animation.keys.map(
                  (keyframe: { frame: number }) =>
                    keyframe.frame / (animation.framePerSecond || FPS),
                );

                // 3. Handle metadata adjustments
                if (group.metadata?.uvOffsets && upsideValue !== undefined) {
                  // fill in uv offsets if available based on dieType
                  let offsetIndex = upsideValue;
                  if (dieType === DieTypes.d100) {
                    offsetIndex = upsideValue ? upsideValue / 10 : 0;
                  } else if (dieType === DieTypes.d10 && upsideValue === 10) {
                    offsetIndex = 0;
                  }

                  if (animation.property.includes("uOffset")) {
                    animation.keys = [
                      {
                        frame: 0,
                        values: [group.metadata?.uvOffsets[offsetIndex][0]],
                      },
                    ];
                  } else if (animation.property.includes("vOffset")) {
                    animation.keys = [
                      {
                        frame: 0,
                        values: [group.metadata?.uvOffsets[offsetIndex][1]],
                      },
                    ];
                  }
                }

                // 4. Create track(s) for this part of the animation
                const track = buildTrack(animation, targetName, times);
                if (Array.isArray(track)) {
                  tracks.push(...track);
                } else if (track) {
                  tracks.push(track);
                }
              });

              const duration = endFrame ? endFrame / FPS : -1;

              if (isDebug) {
                console.log("Created animation clip", trigger.definition);
              }

              return new AnimationClip(
                trigger.definition ?? "diceDissolve",
                duration,
                tracks,
              );
            })
            .filter((clip) => clip !== undefined),
    [triggers, animationGroups, setId, rootObject, upsideValue, dieType],
  );

  return clips;
};
